/**
 * Row and Batch Validation
 *
 * Orchestrates validation of CSV rows against column definitions.
 * Uses the cell validator for individual cell validation and aggregates
 * results at the row and batch level.
 *
 * - validateRow: validates a single row, returns per-cell results
 * - validateRows: validates all rows, splits into valid/invalid with summary
 *
 * This module is pure, synchronous, and side-effect-free.
 */

import type { NormalizedColumnDefinition } from '../types/template';
import { validateCell, type CellValidationResult } from './cell-validator';

/** Machine-readable error code for row-level validation failures */
export type RowErrorCode = 'duplicate';

/** Result of validating a single row */
export interface RowValidationResult {
  /** Original row index (1-based, excluding header) */
  rowIndex: number;
  /** Whether all cells in the row are valid */
  valid: boolean;
  /** Validation result per column key */
  cells: Record<string, CellValidationResult>;
  /** Transformed row data (only meaningful when valid) */
  data: Record<string, string>;
  /** Whether this row is a duplicate of a prior row */
  duplicate: boolean;
  /** rowIndex of the first occurrence, or null if not a duplicate */
  duplicateOfRow: number | null;
  /** Row-level error code, or null */
  errorCode: RowErrorCode | null;
}

/** Result of validating all rows */
export interface BatchValidationResult {
  /** Rows that passed all validation */
  validRows: Record<string, string>[];
  /** 1-based file row index for each entry in validRows (parallel array). */
  validRowIndices: number[];
  /** Rows that failed with error details */
  invalidRows: RowValidationResult[];
  /** Summary counts */
  totalRows: number;
  validCount: number;
  invalidCount: number;
  /** Number of rows that passed cell validation but were flagged as duplicates */
  duplicateCount: number;
}

/**
 * Validates a single row against all column definitions.
 *
 * @param row - A single parsed CSV row as key-value pairs
 * @param columns - Normalized column definitions
 * @param rowIndex - 1-based row number (excluding header)
 * @returns Row validation result with per-cell details
 */
export function validateRow(
  row: Record<string, string>,
  columns: NormalizedColumnDefinition[],
  rowIndex: number,
): RowValidationResult {
  const cells: Record<string, CellValidationResult> = {};
  const data: Record<string, string> = {};
  let valid = true;

  for (const column of columns) {
    const value = row[column.key] ?? '';
    const result = validateCell(value, column);
    cells[column.key] = result;
    data[column.key] = result.transformedValue;
    if (!result.valid) valid = false;
  }

  return {
    rowIndex,
    valid,
    cells,
    data,
    duplicate: false, // always false from validateRow
    duplicateOfRow: null, // always null from validateRow
    errorCode: null, // always null from validateRow
  };
}

/**
 * Detects duplicate rows among cell-valid rows using a two-phase algorithm.
 *
 * Phase 1 (DETECT): Check all unique columns without mutating seenMaps.
 * Phase 2 (REGISTER or DISCARD): Only register values for rows that are kept.
 *
 * This ensures `duplicateOfRow` always references a row in `validRows`,
 * never a discarded duplicate.
 *
 * @param cellValidRows - Rows that passed cell-level validation
 * @param columns - Normalized column definitions (definition order matters)
 * @returns Valid row data and duplicate row results
 */
function detectDuplicates(
  cellValidRows: RowValidationResult[],
  columns: NormalizedColumnDefinition[],
): {
  validRows: Record<string, string>[];
  validRowIndices: number[];
  duplicateRows: RowValidationResult[];
} {
  // One Map per unique column: value → rowIndex of first occurrence
  const uniqueColumns = columns.filter((col) => col.unique);
  const seenMaps: Map<string, Map<string, number>> = new Map(
    uniqueColumns.map((col) => [col.key, new Map()]),
  );

  const validRows: Record<string, string>[] = [];
  const validRowIndices: number[] = [];
  const duplicateRows: RowValidationResult[] = [];

  for (const row of cellValidRows) {
    // Phase 1: DETECT — check all unique columns without mutating seenMaps
    let duplicateOfRow: number | null = null;

    for (const col of uniqueColumns) {
      const value = row.data[col.key] ?? '';
      if (value === '') continue; // empty values don't participate

      const hit = seenMaps.get(col.key)!.get(value);
      if (hit !== undefined) {
        duplicateOfRow = hit;
        break; // first match in column definition order
      }
    }

    // Phase 2: REGISTER or DISCARD
    if (duplicateOfRow !== null) {
      // Discard — do NOT register this row's values in seenMaps
      row.valid = false;
      row.duplicate = true;
      row.duplicateOfRow = duplicateOfRow;
      row.errorCode = 'duplicate';
      duplicateRows.push(row);
    } else {
      // Keep — register this row's values for future duplicate checks
      for (const col of uniqueColumns) {
        const value = row.data[col.key] ?? '';
        if (value !== '') {
          seenMaps.get(col.key)!.set(value, row.rowIndex);
        }
      }
      validRows.push(row.data);
      validRowIndices.push(row.rowIndex);
    }
  }

  return { validRows, validRowIndices, duplicateRows };
}

/**
 * Validates all rows and splits into valid/invalid.
 *
 * Performs cell-level validation on each row, then runs batch-level
 * duplicate detection on cell-valid rows. Results are combined with
 * invalidRows sorted by rowIndex.
 *
 * @param rows - Array of parsed CSV rows from PapaParse
 * @param columns - Normalized column definitions
 * @returns Batch result with valid rows, invalid rows with errors, and summary counts
 */
export function validateRows(
  rows: Record<string, string>[],
  columns: NormalizedColumnDefinition[],
): BatchValidationResult {
  const cellValidRows: RowValidationResult[] = [];
  const cellInvalidRows: RowValidationResult[] = [];

  // Phase 1: Cell validation (existing logic)
  for (let i = 0; i < rows.length; i++) {
    const result = validateRow(rows[i]!, columns, i + 1);
    if (result.valid) {
      cellValidRows.push(result);
    } else {
      cellInvalidRows.push(result);
    }
  }

  // Phase 2: Duplicate detection (only on cell-valid rows)
  const { validRows, validRowIndices, duplicateRows } = detectDuplicates(
    cellValidRows,
    columns,
  );

  // Phase 3: Build result with counts
  // Sort invalidRows by rowIndex to maintain original file order
  const invalidRows = [...cellInvalidRows, ...duplicateRows].sort(
    (a, b) => a.rowIndex - b.rowIndex,
  );

  return {
    validRows,
    validRowIndices,
    invalidRows,
    totalRows: rows.length,
    validCount: validRows.length,
    invalidCount: cellInvalidRows.length,
    duplicateCount: duplicateRows.length,
  };
}
